import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, Money, EmergencyBanner } from "@/components/shared/Common";
import { Search, Filter, Star, ArrowRight, Languages } from "lucide-react";

export default function FindCare() {
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [type, setType] = useState("");
  const [gender, setGender] = useState("");
  const [language, setLanguage] = useState("");

  useEffect(() => {
    api.get("/specialties").then(r => setSpecialties(r.data));
    api.get("/doctors").then(r => setDoctors(r.data));
  }, []);

  const filtered = doctors.filter(d => {
    const name = `${d.first_name} ${d.last_name}`.toLowerCase();
    if (q && !name.includes(q.toLowerCase()) && !(d.specialty || "").includes(q.toLowerCase())) return false;
    if (specialty && d.specialty !== specialty) return false;
    if (type && !(d.consultation_types || []).includes(type)) return false;
    if (gender && d.gender !== gender) return false;
    if (language && !(d.languages || []).map(l => l.toLowerCase()).includes(language.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="cm-container py-12">
      <div className="mb-6"><EmergencyBanner compact /></div>
      <div className="mb-8">
        <div className="cm-eyebrow mb-2">Find care</div>
        <h1 className="cm-h2">The right doctor, matched to you.</h1>
        <p className="cm-body mt-2 max-w-2xl">Filter by specialty, availability, price and language. Every doctor is GMC-verified.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <aside className="cm-card lg:sticky lg:top-24 h-fit" data-testid="find-care-filters">
          <div className="flex items-center gap-2 mb-4"><Filter size={16} className="text-[#4a90d9]"/><h3 className="font-semibold text-[#1a2b4a]">Filters</h3></div>

          <label className="cm-label">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-[#94a3b8]"/>
            <input className="cm-input pl-9" placeholder="Name or specialty" value={q} onChange={e => setQ(e.target.value)} data-testid="filter-search"/>
          </div>

          <div className="mt-4">
            <label className="cm-label">Specialty</label>
            <select className="cm-input" value={specialty} onChange={e => setSpecialty(e.target.value)} data-testid="filter-specialty">
              <option value="">All specialties</option>
              {specialties.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </div>

          <div className="mt-4">
            <label className="cm-label">Consultation type</label>
            <select className="cm-input" value={type} onChange={e => setType(e.target.value)} data-testid="filter-type">
              <option value="">Any</option>
              <option value="general">General (£50)</option>
              <option value="specialist">Specialist (£70)</option>
            </select>
          </div>

          <div className="mt-4">
            <label className="cm-label">Gender preference</label>
            <select className="cm-input" value={gender} onChange={e => setGender(e.target.value)} data-testid="filter-gender">
              <option value="">No preference</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </div>

          <div className="mt-4">
            <label className="cm-label">Language</label>
            <input className="cm-input" placeholder="e.g. English, Hindi, Arabic" value={language} onChange={e => setLanguage(e.target.value)} data-testid="filter-language"/>
          </div>

          <button className="cm-btn-outline w-full mt-5" onClick={() => { setQ(""); setSpecialty(""); setType(""); setGender(""); setLanguage(""); }} data-testid="filter-reset">Reset filters</button>
        </aside>

        <div className="lg:col-span-3">
          <div className="text-sm text-[#475569] mb-3">{filtered.length} doctors available</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {filtered.map(d => (
              <DoctorCard key={d.id} d={d}/>
            ))}
            {filtered.length === 0 && <div className="cm-card text-sm text-[#475569]">No doctors match those filters yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function DoctorCard({ d }) {
  return (
    <Link to={`/doctors/${d.user_id}`} className="cm-card block hover:-translate-y-0.5" data-testid={`doctor-card-${d.user_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#f5f7fa] flex-shrink-0">
          {d.photo ? <img src={d.photo} alt={d.first_name} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-[#4a90d9] text-xl font-bold">{(d.first_name||"?")[0]}</div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-[#1a2b4a]">Dr. {d.first_name} {d.last_name}</div>
              <div className="text-xs text-[#4a90d9] capitalize font-medium">{(d.specialty || "").replace("_"," ")}</div>
            </div>
            <StatusBadge status={d.verification_status}/>
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-[#475569]">
            {d.rating > 0 && <span className="flex items-center gap-1"><Star size={12} className="text-[#d97706] fill-current"/>{Number(d.rating).toFixed(1)}</span>}
            {d.reviews_count > 0 && <span>{d.reviews_count} reviews</span>}
            {d.years_experience > 0 && <span>{d.years_experience} yrs</span>}
          </div>
          {d.languages?.length > 0 && (
            <div className="mt-2 flex items-center gap-1 text-xs text-[#475569]"><Languages size={12}/> {d.languages.slice(0,3).join(", ")}</div>
          )}
          <p className="text-xs text-[#475569] mt-2 line-clamp-2">{d.bio}</p>
          <div className="mt-3 flex items-center justify-end">
            <span className="text-xs font-semibold text-[#4a90d9] inline-flex items-center gap-1">View profile <ArrowRight size={12}/></span>
          </div>
        </div>
      </div>
    </Link>
  );
}
